import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { clubsAPI, playersAPI, venuesAPI } from '../lib/api';
import { formatDate, getInitials } from '../lib/utils';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Users, Plus, MapPin, ChevronRight, Building, CreditCard, BarChart3, Swords, Trophy } from 'lucide-react';

export default function Clubs() {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [clubs, setClubs] = useState([]);
  const [players, setPlayers] = useState({});
  const [venues, setVenues] = useState([]);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [showAddVenue, setShowAddVenue] = useState(false);
  const [newVenue, setNewVenue] = useState({ name: '', address: '', courts: 1 });
  
  const [newClub, setNewClub] = useState({
    name: '',
    description: '',
    venue_id: '',
    is_academy: false,
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [clubsRes, playersRes, venuesRes] = await Promise.all([
          clubsAPI.getAll(),
          playersAPI.getAll(),
          venuesAPI.getAll(),
        ]);
        
        setClubs(clubsRes.data || []);
        setVenues(venuesRes.data || []);
        
        const playersLookup = {};
        (playersRes.data || []).forEach((p) => {
          playersLookup[p.user_id] = p;
        });
        setPlayers(playersLookup);
      } catch (error) {
        console.error('Error fetching clubs:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleCreateClub = async () => {
    if (!newClub.name.trim()) {
      toast.error('Club name is required');
      return;
    }

    setCreating(true);
    try {
      const response = await clubsAPI.create({
        name: newClub.name,
        description: newClub.description || null,
        venue_id: newClub.venue_id && newClub.venue_id !== 'none' ? newClub.venue_id : null,
        is_academy: newClub.is_academy,
      });
      
      setClubs([response.data, ...clubs]);
      setCreateDialogOpen(false);
      setNewClub({ name: '', description: '', venue_id: '', is_academy: false });
      toast.success('Club created successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create club');
    } finally {
      setCreating(false);
    }
  };

  const handleAddVenueInline = async () => {
    if (!newVenue.name.trim()) { toast.error('Venue name required'); return; }
    try {
      const res = await venuesAPI.create({ name: newVenue.name, address: newVenue.address, courts: parseInt(newVenue.courts) || 1 });
      const created = res.data;
      setVenues([...venues, created]);
      setNewClub({ ...newClub, venue_id: created.venue_id });
      setShowAddVenue(false);
      setNewVenue({ name: '', address: '', courts: 1 });
      toast.success('Venue added');
    } catch (err) {
      toast.error('Failed to create venue');
    }
  };

  const getPlayerName = (playerId) => {
    return players[playerId]?.name || 'Unknown';
  };

  const getVenueName = (venueId) => {
    const venue = venues.find((v) => v.venue_id === venueId);
    return venue?.name || 'No venue';
  };

  if (loading) {
    return (
      <Layout>
        <div className="space-y-6">
          <Skeleton className="h-8 w-48" />
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-48" />
            ))}
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="clubs-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold uppercase tracking-tight">{t('clubs.title')}</h1>
            <p className="text-muted-foreground text-sm mt-1">
              {clubs.length} {t('clubs.title').toLowerCase()} {t('clubs.registered')}
            </p>
          </div>
          
          <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-secondary hover:bg-secondary/90" data-testid="create-club-btn"
                onClick={(e) => { if (!isAuthenticated) { e.preventDefault(); navigate('/login'); } }}>
                <Plus className="mr-2 h-4 w-4" />
                Create Club
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Create New Club</DialogTitle>
                <DialogDescription>
                  Set up your badminton club or academy
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Club Name *</Label>
                  <Input
                    id="name"
                    placeholder="e.g., Shuttle Stars Club"
                    value={newClub.name}
                    onChange={(e) => setNewClub({ ...newClub, name: e.target.value })}
                    data-testid="club-name-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    placeholder="Tell us about your club..."
                    value={newClub.description}
                    onChange={(e) => setNewClub({ ...newClub, description: e.target.value })}
                    data-testid="club-description-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="venue">Home Venue</Label>
                  <Select
                    value={newClub.venue_id}
                    onValueChange={(value) => {
                      if (value === '__add_new__') { setShowAddVenue(true); return; }
                      setNewClub({ ...newClub, venue_id: value });
                    }}
                  >
                    <SelectTrigger data-testid="club-venue-select">
                      <SelectValue placeholder="Select venue (optional)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No venue</SelectItem>
                      {venues.map((venue) => (
                        <SelectItem key={venue.venue_id} value={venue.venue_id}>
                          {venue.name}
                        </SelectItem>
                      ))}
                      <SelectItem value="__add_new__" className="text-secondary font-medium">+ Add New Venue</SelectItem>
                    </SelectContent>
                  </Select>
                  {showAddVenue && (
                    <div className="border rounded-lg p-3 space-y-2 bg-muted/50">
                      <Input placeholder="Venue name *" value={newVenue.name} onChange={e => setNewVenue({...newVenue, name: e.target.value})} data-testid="inline-venue-name" />
                      <Input placeholder="Address" value={newVenue.address} onChange={e => setNewVenue({...newVenue, address: e.target.value})} />
                      <div className="flex gap-2">
                        <Button size="sm" onClick={handleAddVenueInline} className="bg-secondary hover:bg-secondary/90">Save Venue</Button>
                        <Button size="sm" variant="outline" onClick={() => setShowAddVenue(false)}>Cancel</Button>
                      </div>
                    </div>
                  )}
                </div>
                
                <div className="flex items-center space-x-2">
                  <Switch
                    id="academy"
                    checked={newClub.is_academy}
                    onCheckedChange={(checked) => setNewClub({ ...newClub, is_academy: checked })}
                    data-testid="club-academy-switch"
                  />
                  <Label htmlFor="academy">This is a training academy</Label>
                </div>
              </div>
              
              <DialogFooter>
                <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>
                  Cancel
                </Button>
                <Button
                  onClick={handleCreateClub}
                  disabled={creating}
                  className="bg-secondary hover:bg-secondary/90"
                  data-testid="submit-club-btn"
                >
                  {creating ? 'Creating...' : 'Create Club'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {/* Clubs Grid */}
        {clubs.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Building className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
              <p className="text-muted-foreground">No clubs yet. Create one to get started!</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {clubs.map((club) => {
              const isManager = user?.role === 'platform_admin' || user?.role === 'club_admin' || club.manager_id === user?.user_id;
              const isMember = isManager || (club.member_ids || []).includes(user?.user_id);
              return (
                <Card key={club.club_id} className="hover:shadow-md hover:border-primary/50 transition-all h-full">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center">
                          <Building className="h-6 w-6 text-primary" />
                        </div>
                        <div>
                          <h3 className="font-heading font-bold text-lg">{club.name}</h3>
                          {club.is_academy && (
                            <Badge variant="outline" className="text-xs">Academy</Badge>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {club.description && (
                      <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                        {club.description}
                      </p>
                    )}
                    
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Users className="h-4 w-4" />
                        <span>{club.member_ids?.length || 0} members</span>
                      </div>
                      {club.venue_id && (
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <MapPin className="h-4 w-4" />
                          <span>{getVenueName(club.venue_id)}</span>
                        </div>
                      )}
                    </div>
                    
                    {/* Member Avatars */}
                    {club.member_ids?.length > 0 && (
                      <div className="flex -space-x-2 mt-4">
                        {club.member_ids.slice(0, 5).map((memberId) => (
                          <Avatar key={memberId} className="h-8 w-8 border-2 border-background">
                            <AvatarImage src={players[memberId]?.picture} />
                            <AvatarFallback className="text-xs">
                              {getInitials(getPlayerName(memberId))}
                            </AvatarFallback>
                          </Avatar>
                        ))}
                        {club.member_ids.length > 5 && (
                          <div className="h-8 w-8 rounded-full bg-muted flex items-center justify-center text-xs font-medium border-2 border-background">
                            +{club.member_ids.length - 5}
                          </div>
                        )}
                      </div>
                    )}
                    
                    {/* Membership Management + Leaderboard Links */}
                    <div className="flex gap-2 mt-4">
                      {isManager && (
                        <Link 
                          to={`/clubs/${club.club_id}/membership`} 
                          onClick={(e) => e.stopPropagation()}
                          data-testid={`manage-membership-${club.club_id}`}
                          className="flex-1"
                        >
                          <Button variant="outline" size="sm" className="w-full">
                            <CreditCard className="h-4 w-4 mr-2" />
                            {t('clubs.manageMembership')}
                          </Button>
                        </Link>
                      )}
                      <Link 
                        to={`/clubs/${club.club_id}/leaderboard`}
                        onClick={(e) => e.stopPropagation()}
                        data-testid={`club-leaderboard-${club.club_id}`}
                        className={isManager ? '' : 'flex-1'}
                      >
                        <Button variant="outline" size="sm" className="w-full">
                          <BarChart3 className="h-4 w-4 mr-2" />
                          Leaderboard
                        </Button>
                      </Link>
                    </div>
                    {/* Club Actions: Create Match, Create Tournament - Only for members/managers */}
                    {isMember && (
                    <div className="flex gap-2 mt-2">
                      <Button
                        variant="outline" size="sm" className="flex-1"
                        onClick={(e) => { e.stopPropagation(); if (!isAuthenticated) { navigate('/login'); } else { navigate(`/matches/new?club=${club.club_id}`); } }}
                        data-testid={`club-create-match-${club.club_id}`}
                      >
                        <Swords className="h-3.5 w-3.5 mr-1" />Create Match
                      </Button>
                      <Button
                        variant="outline" size="sm" className="flex-1"
                        onClick={(e) => { e.stopPropagation(); if (!isAuthenticated) { navigate('/login'); } else { navigate(`/tournaments?create=true&club=${club.club_id}`); } }}
                        data-testid={`club-create-tournament-${club.club_id}`}
                      >
                        <Trophy className="h-3.5 w-3.5 mr-1" />Create Tournament
                      </Button>
                    </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </Layout>
  );
}
